/**
 * The playable classes (MVP scope) — the single source of truth is
 * `classes.store` (repo root); this module imports its generated projection
 * `classes.data.ts` (do not edit — save in `npm run knobs` or run
 * `node tools/classes-store.js`).
 *
 * Three classes, five levels, three abilities each (docs/design/classes.md).
 * Leveling does not ADD abilities — it scales the three (bigger numbers,
 * shorter cooldowns) via the per-level tables here; level also scales bare
 * combat through the level row (atkBonus adds ATTACK POINTS to the weapon's
 * attack — the contest value, replaces the old multiplicative accMod; dmgMod
 * still scales the damage maximum).
 *
 * THE SIM DISPATCHES ON THE KIND (sim/step.ts `useAbility`): the Knight's
 * (next-hit-bonus / damage-immune / phoenix-teleport — the Knight's splash
 * kind, `next-hit-splash`, is BENCHED: implemented, off the bar since
 * 2026-11-04, see classes.store), the Mage's
 * (damage-reflect / next-hit-chain / room-aoe) and the Squire's (speed-buff /
 * teleport / telegrab) kinds are implemented; `next-hit-reduce` is reserved
 * and still rejects. `CLASSES` / `HeroClass` / `isHeroClass` are unchanged
 * from the accounts-phase stub: the creator stores a class at account
 * creation and the wire values never moved.
 *
 * All numbers are PROVISIONAL (same status as combat balance — retune in
 * `npm run arena` and the knobs app). The parser is pure text-in /
 * data-out (no fs, no read of CLASSES_DATA), shared by tools/ and the knobs
 * server, like parseCombatStore.
 */
import { CLASSES_DATA } from "./classes.data.js";
import type { WeaponClass } from "./items.js";

export const CLASSES = ["knight", "mage", "squire"] as const;
export type HeroClass = (typeof CLASSES)[number];

export const isHeroClass = (v: unknown): v is HeroClass =>
  typeof v === "string" && (CLASSES as readonly string[]).includes(v);

/** Class level bounds (docs/design/classes.md). */
export const CLASS_MIN_LEVEL = 1;
export const CLASS_MAX_LEVEL = 5;

/**
 * PLAYTEST OVERRIDE (early playtest): levels are not part of the game people
 * are playing — every hero plays at the MAX level, so the whole kit (ability
 * tiers, the per-level HP table, the level mods) is live without an XP system.
 *
 * The level MODEL stays: the store, the per-level tables, the fixtures that
 * pin a level, and the arena's level control all keep working. What is gone is
 * the live game's level CHOICE — the server pins it at the account boundary
 * (`net/server.ts`, `net/room.ts`) and the Bull's editor no longer offers the
 * picker. When XP earning returns, delete this constant and the call sites
 * that read it.
 */
export const PLAYTEST_HERO_LEVEL = CLASS_MAX_LEVEL;

/**
 * What an ability DOES — one per slot (the sim's UseAbility switch resolves
 * on this, never on the class's name). Semantics per the store header;
 * `extra` (radius / duration) is required for the kinds that use it.
 *
 * The Mage's kit (docs/design/classes.md):
 * - damage-reflect — for `extra` ticks, absorb `magnitude` percent of
 *                 incoming damage and return that amount to its attacker.
 * - pull-vortex — fires on cast at an AIMED tile (the cast carries it): every
 *                 enemy within `extra` (radius) of the tile takes a normal
 *                 weapon roll, and every MOVABLE one is dragged into a clump
 *                 on the tile (immovables stay put but are still hit). Gated
 *                 on a magic weapon. The well never misses; the hit in it can.
 * - next-hit-chain — charges the NEXT hit (any weapon); when the primary
 *                 CONNECTS it simultaneously ricochets from the struck enemy
 *                 to every other enemy within `extra` (radius) of it that the
 *                 target can see — each a normal weapon roll. A miss breaks
 *                 the chain.
 * - room-aoe — fires on cast: every living enemy in the hero's ROOM takes a
 *                 normal weapon roll (no range, sight or radius — the room is
 *                 the range). No `extra`.
 *
 * The Squire's kit (docs/design/classes.md):
 * - speed-buff — fires on cast: the hero's walk cadence drops to
 *                 `moveCostTicks × 100 / magnitude` (integer, rounded,
 *                 minimum 1) for `extra` ticks. Self only.
 * - teleport — fires on cast at an AIMED tile: blink to a revealed walkable
 *                 tile in the hero's CURRENT room (the room is the range —
 *                 no line-of-sight check: a teleport crosses cover). No
 *                 `extra`.
 * - telegrab — fires on cast on a FLOOR ITEM (the packet's targetKind +
 *                 targetId): pull that item (a key, a gatestone) straight
 *                 into the hero's inventory from anywhere in the hero's
 *                 current room — no sight, no distance, no walking path.
 *                 The inventory cap still applies. No `extra`.
 *
 * The Knight's third ability is `phoenix-teleport` (docs/design/classes.md):
 * - phoenix-teleport — fires on cast with NO aim: the hero burns back to the
 *                 tile where THEY last died on THIS floor. The death is the
 *                 gate, not the cooldown row: the cast is impossible until the
 *                 hero has died once on this floor and is alive again, and
 *                 every cast spends the charge (dark again until the next
 *                 death). The destination is the hero's own death tile, so it
 *                 crosses room boundaries and cover (the death already walked
 *                 there); nothing about the tile is re-checked — the hero
 *                 stood on it, so it is walkable, in a revealed room, and not
 *                 a door strip. No `extra`.
 */
export type AbilityKind =
  | "next-hit-bonus"
  | "next-hit-reduce"
  | "next-hit-splash"
  | "damage-immune"
  | "damage-reflect"
  | "pull-vortex"
  | "next-hit-chain"
  | "room-aoe"
  | "speed-buff"
  | "teleport"
  | "telegrab"
  | "phoenix-teleport";

export const ABILITY_KINDS = [
  "next-hit-bonus",
  "next-hit-reduce",
  "next-hit-splash",
  "damage-immune",
  "damage-reflect",
  "pull-vortex",
  "next-hit-chain",
  "room-aoe",
  "speed-buff",
  "teleport",
  "telegrab",
  "phoenix-teleport",
] as const;

/**
 * Kinds whose level rows carry `extra` (radius in tiles / duration in ticks).
 * `damage-immune`: `extra` is the immunity window in TICKS, `magnitude` is the
 * incoming-damage reduction % during that window (100 = negate all damage).
 * Exported so the store tests assert against the source of truth, not a
 * hardcoded copy (a new extra-requiring kind updates here and the test follows).
 */
export const EXTRA_KINDS = new Set<AbilityKind>(["pull-vortex", "next-hit-chain", "speed-buff", "damage-immune", "damage-reflect"]);

/**
 * The kinds cast ON A TILE: the `UseAbility` packet carries `x`/`y` (the
 * aimed tile, both-or-neither) and the client's aim-mode UX reads this list,
 * so a new aimed kind lands in one place. The SIM still validates per kind
 * (pull-vortex wants a walkable tile in sight; teleport wants one in the
 * hero's current room), because what a tile must satisfy is the ability's
 * rule, not the packet's shape. `telegrab` is here for the aim UX (the
 * reticle highlights item tiles) even though the cast carries an item
 * reference — the tile is only the click target.
 */
export const TILE_AIMED_KINDS: readonly AbilityKind[] = ["pull-vortex", "teleport", "telegrab"];

/** One level of one ability (index = level − 1). All integers:
 *  magnitude ×100 percents (100 = ×1.0), cooldown in ticks (20 = 1 s),
 *  extra per the kind (radius / duration) or null. */
export interface AbilityLevel {
  magnitude: number;
  cooldown: number;
  extra: number | null;
}

/** A class's one ability: fixed slot id 0–2, always known. */
export interface AbilityDef {
  id: number;
  name: string;
  kind: AbilityKind;
  /** THE ABILITY'S ATTACK TYPE (the contest bucket picker — same axis as the
   *  weapon `class`): melee → the target's meleeDef, magic → its magicDef
   *  (ranged reserved, folds into meleeDef by the same rule as weapons).
   *  Defaults to "melee" in the parser; a Mage's spell sets `magic`.
   *  Presentation-independent: a melee-type ability on any class still
   *  contests meleeDef whichever animation it wears. */
  atkClass: WeaponClass;
  /** Indexed by level − 1 (CLASS_MIN_LEVEL..CLASS_MAX_LEVEL). */
  levels: AbilityLevel[];
}

export interface ClassDef {
  kind: HeroClass;
  name: string;
  meleeWeaponEfficiencyPct: number;
  magicWeaponEfficiencyPct: number;
  /** Exactly three, ids 0..2. */
  abilities: AbilityDef[];
}

/** The bare-combat scale per level (heroStats' atkBonus/dmgMod source).
 *  `maxHp` is the BARE-HERO max HP at that level — the base the armor's
 *  maxHp adds onto (the hero's HP lives in the level table, not the combat
 *  table: one number, one place to edit it). */
export interface LevelRow {
  level: number;
  /** ATTACK POINTS added on top of the weapon's attack (the accuracy
   *  contest's level lever — RS-style "+levels", flat and readable: +3 is
   *  +3 no matter what the weapon says). Replaces the multiplicative
   *  accMod, which made level power depend on the authored accuracy. */
  atkBonus: number;
  dmgMod: number;
  maxHp: number;
}

export interface ClassesData {
  /** Cumulative total XP to REACH level n+1 (index = level − 1). */
  xp: number[];
  levels: LevelRow[];
  classes: ClassDef[];
}

function validateData(d: ClassesData): ClassesData {
  // Compatibility for projections made before proficiency was introduced.
  d = { ...d, classes: d.classes.map((c) => ({ ...c,
    meleeWeaponEfficiencyPct: c.meleeWeaponEfficiencyPct ?? 100,
    magicWeaponEfficiencyPct: c.magicWeaponEfficiencyPct ?? 100,
  })) };
  if (d.xp.length !== CLASS_MAX_LEVEL) {
    throw new Error(`classes: xp curve has ${d.xp.length} entries (want ${CLASS_MAX_LEVEL})`);
  }
  if (d.xp[0] !== 0) throw new Error("classes: xp curve must start at 0");
  for (let i = 1; i < d.xp.length; i++) {
    if (d.xp[i]! < d.xp[i - 1]!) throw new Error(`classes: xp curve must be non-decreasing (level ${i + 1})`);
  }
  if (d.levels.length !== CLASS_MAX_LEVEL) {
    throw new Error(`classes: level mods have ${d.levels.length} rows (want ${CLASS_MAX_LEVEL})`);
  }
  d.levels.forEach((r, i) => {
    if (r.level !== i + 1) throw new Error(`classes: level row ${i} says level ${r.level}`);
    if (r.atkBonus < 0 || r.dmgMod < 1) throw new Error(`classes: level ${r.level} atkBonus must be >= 0 / dmgMod must be >= 1`);
    if (r.maxHp < 1) throw new Error(`classes: level ${r.level} maxHp must be >= 1`);
  });
  if (d.classes.length !== CLASSES.length) {
    throw new Error(`classes: ${d.classes.length} classes (want ${CLASSES.join(" | ")})`);
  }
  const seen = new Set<HeroClass>();
  for (const c of d.classes) {
    if (!isHeroClass(c.kind)) throw new Error(`classes: unknown class kind "${String(c.kind)}"`);
    if (seen.has(c.kind)) throw new Error(`classes: duplicate class ${c.kind}`);
    seen.add(c.kind);
    for (const key of ["meleeWeaponEfficiencyPct", "magicWeaponEfficiencyPct"] as const) {
      if (!Number.isSafeInteger(c[key]) || c[key] < 0 || c[key] > 1000) {
        throw new Error(`classes: ${c.kind} ${key} must be an integer 0..1000`);
      }
    }
    if (c.name.trim() === "") throw new Error(`classes: ${c.kind} has an empty name`);
    if (c.abilities.length !== 3) throw new Error(`classes: ${c.kind} has ${c.abilities.length} abilities (want 3)`);
    c.abilities.forEach((a, i) => {
      if (a.id !== i) throw new Error(`classes: ${c.kind} ability slot ${i} has id ${a.id}`);
      if (a.name.trim() === "") throw new Error(`classes: ${c.kind} ability ${i} has an empty name`);
      if (!ABILITY_KINDS.includes(a.kind)) throw new Error(`classes: ${c.kind} ability ${a.id} has unknown kind "${a.kind}"`);
      if (a.atkClass !== "melee" && a.atkClass !== "magic" && a.atkClass !== "ranged") {
        throw new Error(`classes: ${c.kind} ability ${a.id} has unknown atkClass "${String(a.atkClass)}" (want melee | magic | ranged)`);
      }
      if (a.levels.length !== CLASS_MAX_LEVEL) {
        throw new Error(`classes: ${c.kind}.${a.name} has ${a.levels.length} levels (want ${CLASS_MAX_LEVEL})`);
      }
      const wantExtra = EXTRA_KINDS.has(a.kind);
      for (const lv of a.levels) {
        if (lv.magnitude < 1 || lv.cooldown < 1) {
          throw new Error(`classes: ${c.kind}.${a.name} magnitude/cooldown must be >= 1`);
        }
        if (a.kind === "damage-reflect" && lv.magnitude > 100) {
          throw new Error(`classes: ${c.kind}.${a.name} reflect magnitude must be <= 100`);
        }
        if (wantExtra && lv.extra === null) {
          throw new Error(`classes: ${c.kind}.${a.name} (${a.kind}) needs an extra (radius/duration) on every level`);
        }
        if (!wantExtra && lv.extra !== null) {
          throw new Error(`classes: ${c.kind}.${a.name} (${a.kind}) takes no extra — drop the third number`);
        }
      }
    });
  }
  return d;
}

/** The committed class tables (classes.store's projection), validated at
 *  module load: a bad store is a build error, never a runtime surprise. */
export const CLASSES_DATA_VALIDATED: ClassesData = validateData(
  CLASSES_DATA as unknown as ClassesData,
);

/** Cumulative total XP to REACH level n+1 (index = level − 1). */
export const XP_CURVE: readonly number[] = CLASSES_DATA_VALIDATED.xp;

/** The bare-combat scale per level. */
export const LEVEL_MODS: readonly LevelRow[] = CLASSES_DATA_VALIDATED.levels;

/** The three kits, keyed by class kind (slot order preserved). */
export const CLASS_DEFS: Readonly<Record<HeroClass, ClassDef>> = (() => {
  const out = {} as Record<HeroClass, ClassDef>;
  for (const c of CLASSES_DATA_VALIDATED.classes) out[c.kind] = c;
  return out;
})();

/**
 * The class catalog for ONE run: the three class definitions, the bare-combat
 * level table and the XP curve — everything the sim reads from classes.store.
 *
 * The sim NEVER reads the module constants directly: `state.classes` carries
 * the catalog, so a caller can pin it. That is the same contract the item /
 * NPC / combat tables already have (`state.catalog` / `state.npcs` /
 * `state.combat`) and the same reason: classes.store is USER CONTENT that a
 * human retunes freely in the knobs app, so a scenario fixture must be able to
 * say exactly which numbers it exercises instead of inheriting whatever the
 * store happens to hold today (`withClassOverrides`, the scenario runner's
 * `config.classRows` / `config.classLevels`).
 */
export interface ClassCatalog {
  defs: Readonly<Record<HeroClass, ClassDef>>;
  /** One row per level, index = level − 1 (heroStats' atkBonus/dmgMod). */
  levels: readonly LevelRow[];
  /** Cumulative XP to reach level n+1 (index = level − 1). */
  xp: readonly number[];
}

/** The committed catalog (classes.store's projection) — the sim's default. */
export const CLASS_CATALOG: ClassCatalog = { defs: CLASS_DEFS, levels: LEVEL_MODS, xp: XP_CURVE };

/**
 * Fixture-side override of a class catalog (the scenario runner's
 * `config.classRows` / `config.classLevels`): a SPARSE merge over `base`, so
 * a fixture pins only the numbers it asserts on and inherits the rest.
 *
 * ```
 * rows:   { knight: { 0: { 1: { magnitude: 220, cooldown: 300 } } } }
 * levels: { 1: { atkBonus: 0, dmgMod: 100 } }
 * ```
 *
 * rows: class kind → ability slot (0–2) → LEVEL (1-based) → the fields to
 * override. levels: level → the bare-combat mods. Both absent (or an empty
 * object) returns `base` untouched. Validation is shared's (a bad fixture is
 * a build error with a readable message, never a runtime surprise): unknown
 * classes/slots/levels, non-integer or < 1 numbers, and a row that would
 * clear or smuggle an `extra` (the kind decides — EXTRA_KINDS) all throw.
 * `base` and the committed projection are never mutated — touched classes and
 * level rows are copied.
 */
export function withClassOverrides(
  base: ClassCatalog,
  overrides: { rows?: unknown; levels?: unknown; proficiencies?: unknown },
): ClassCatalog {
  const rows = overrides.rows;
  const levels = overrides.levels;
  if (rows === undefined && levels === undefined && overrides.proficiencies === undefined) return base;
  const num = (v: unknown, what: string, min = 1): number => {
    if (typeof v !== "number" || !Number.isInteger(v) || v < min) {
      throw new Error(`classCatalog: ${what} must be an integer >= ${min} (got ${JSON.stringify(v)})`);
    }
    return v;
  };

  let defs = base.defs;
  if (rows !== undefined) {
    if (typeof rows !== "object" || rows === null || Array.isArray(rows)) {
      throw new Error("classCatalog.rows must be an object { class: { slot: { level: {...} } } }");
    }
    const next: Record<string, ClassDef> = { ...base.defs };
    for (const [kind, bySlot] of Object.entries(rows as Record<string, unknown>)) {
      if (!isHeroClass(kind)) throw new Error(`classCatalog.rows: unknown class "${kind}" (knight | mage | squire)`);
      const def = base.defs[kind];
      if (typeof bySlot !== "object" || bySlot === null || Array.isArray(bySlot)) {
        throw new Error(`classCatalog.rows.${kind} must be an object { slot: { level: {...} } }`);
      }
      const abilities = def.abilities.map((a) => ({ ...a, levels: a.levels.map((l) => ({ ...l })) }));
      for (const [slotKey, byLevel] of Object.entries(bySlot as Record<string, unknown>)) {
        const slot = Number(slotKey);
        const ability = Number.isInteger(slot) ? abilities[slot] : undefined;
        if (!ability) {
          throw new Error(`classCatalog.rows.${kind}: unknown ability slot "${slotKey}" (0–${abilities.length - 1})`);
        }
        if (typeof byLevel !== "object" || byLevel === null || Array.isArray(byLevel)) {
          throw new Error(`classCatalog.rows.${kind}.${slotKey} must be an object { level: {...} }`);
        }
        for (const [lvKey, patch] of Object.entries(byLevel as Record<string, unknown>)) {
          const lv = Number(lvKey);
          const row = ability.levels[lv - 1];
          if (!Number.isInteger(lv) || row === undefined) {
            throw new Error(`classCatalog.rows.${kind}.${slotKey}: unknown level "${lvKey}" (1–${CLASS_MAX_LEVEL})`);
          }
          if (typeof patch !== "object" || patch === null || Array.isArray(patch)) {
            throw new Error(`classCatalog.rows.${kind}.${slotKey}.${lvKey} must be an object`);
          }
          const p = patch as { magnitude?: unknown; cooldown?: unknown; extra?: unknown };
          if (p.magnitude !== undefined) row.magnitude = num(p.magnitude, `rows.${kind}.${slotKey}.${lvKey}.magnitude`);
          if (ability.kind === "damage-reflect" && row.magnitude > 100) {
            throw new Error(`classCatalog.rows.${kind}.${slotKey}.${lvKey}: reflect magnitude must be <= 100`);
          }
          if (p.cooldown !== undefined) row.cooldown = num(p.cooldown, `rows.${kind}.${slotKey}.${lvKey}.cooldown`);
          if (p.extra !== undefined) {
            row.extra = p.extra === null ? null : num(p.extra, `rows.${kind}.${slotKey}.${lvKey}.extra`);
          }
          // The kind decides whether a row carries an extra (the same rule the
          // store parser enforces): a fixture can neither clear it on a kind
          // that needs it nor add one to a kind that does not.
          const wantExtra = EXTRA_KINDS.has(ability.kind);
          if (wantExtra && row.extra === null) {
            throw new Error(`classCatalog.rows.${kind}.${slotKey}.${lvKey}: ${ability.kind} requires an extra (radius/duration)`);
          }
          if (!wantExtra && row.extra !== null) {
            throw new Error(`classCatalog.rows.${kind}.${slotKey}.${lvKey}: ${ability.kind} takes no extra`);
          }
        }
      }
      next[kind] = { ...def, abilities };
    }
    defs = next as Readonly<Record<HeroClass, ClassDef>>;
  }

  let levelRows = base.levels;
  if (levels !== undefined) {
    if (typeof levels !== "object" || levels === null || Array.isArray(levels)) {
      throw new Error("classCatalog.levels must be an object { level: { maxHp, atkBonus, dmgMod } }");
    }
    const nextRows = base.levels.map((r) => ({ ...r }));
    for (const [lvKey, patch] of Object.entries(levels as Record<string, unknown>)) {
      const lv = Number(lvKey);
      const row = nextRows[lv - 1];
      if (!Number.isInteger(lv) || row === undefined) {
        throw new Error(`classCatalog.levels: unknown level "${lvKey}" (1–${CLASS_MAX_LEVEL})`);
      }
      if (typeof patch !== "object" || patch === null || Array.isArray(patch)) {
        throw new Error(`classCatalog.levels.${lvKey} must be an object { maxHp, atkBonus, dmgMod }`);
      }
      const p = patch as { maxHp?: unknown; atkBonus?: unknown; dmgMod?: unknown };
      if (p.maxHp !== undefined) row.maxHp = num(p.maxHp, `levels.${lvKey}.maxHp`);
      if (p.atkBonus !== undefined) row.atkBonus = num(p.atkBonus, `levels.${lvKey}.atkBonus`, 0);
      if (p.dmgMod !== undefined) row.dmgMod = num(p.dmgMod, `levels.${lvKey}.dmgMod`);
    }
    levelRows = nextRows;
  }

  if (overrides.proficiencies !== undefined) {
    const raw = overrides.proficiencies;
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) throw new Error("classCatalog.proficiencies must be an object");
    const next = { ...defs };
    for (const [kind, patch] of Object.entries(raw as Record<string, unknown>)) {
      if (!isHeroClass(kind) || !patch || typeof patch !== "object" || Array.isArray(patch)) throw new Error(`classCatalog.proficiencies: bad class ${kind}`);
      const def = { ...next[kind] };
      for (const [key, value] of Object.entries(patch as Record<string, unknown>)) {
        if (key !== "meleeWeaponEfficiencyPct" && key !== "magicWeaponEfficiencyPct") throw new Error(`classCatalog.proficiencies: unknown field ${key}`);
        def[key] = num(value, `proficiencies.${kind}.${key}`, 0);
        if (def[key] > 1000) throw new Error(`classCatalog.proficiencies: ${key} must be <= 1000`);
      }
      next[kind] = def;
    }
    defs = next;
  }
  return { defs, levels: levelRows, xp: base.xp };
}

/** The highest level whose cumulative XP threshold is met (1..5). */
export function levelForXp(xp: number, curve: readonly number[] = XP_CURVE): number {
  let level = CLASS_MIN_LEVEL;
  for (let i = 0; i < curve.length; i++) {
    if (xp >= (curve[i] as number)) level = i + 1;
  }
  return level;
}

/** Cumulative total XP required to reach the level. */
export function xpForLevel(level: number, curve: readonly number[] = XP_CURVE): number {
  if (level < CLASS_MIN_LEVEL || level > CLASS_MAX_LEVEL) {
    throw new Error(`classes: level ${level} out of bounds [${CLASS_MIN_LEVEL}, ${CLASS_MAX_LEVEL}]`);
  }
  return curve[level - 1] as number;
}

/**
 * Parse + validate classes.store text (line-numbered errors, like the other
 * store parsers). PURE — no fs, no read of CLASSES_DATA.
 */
// Mutable parse accumulators (the kind is checked against ABILITY_KINDS
// before the result is cast to the public shape — validateData re-checks).
type MutableAbility = { id: number; name: string; kind: string; atkClass: string; levels: AbilityLevel[] };
type MutableClass = { kind: HeroClass; name: string; meleeWeaponEfficiencyPct: number; magicWeaponEfficiencyPct: number; abilities: MutableAbility[] };

export function parseClassesStore(text: string): { ok: true; data: ClassesData } | { ok: false; errors: string[] } {
  const errors: string[] = [];
  const xp: number[] = [];
  const levels: LevelRow[] = [];
  const classes: MutableClass[] = [];
  let section: "xp" | "levels" | null = null;
  let cur: MutableClass | null = null;
  let curAbility: MutableAbility | null = null;
  const seenSections = new Set<string>();

  const lines = text.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const lineNo = i + 1;
    const line = lines[i]!.trim();
    if (line === "" || line.startsWith("#")) continue;

    const classMatch = /^\[class\]\s+(\S+)$/.exec(line);
    const block = /^\[(xp|levels)\]$/.exec(line);
    if (classMatch) {
      if (seenSections.has(`class:${classMatch[1]}`)) errors.push(`line ${lineNo}: duplicate [class] ${classMatch[1]}`);
      seenSections.add(`class:${classMatch[1]}`);
      if (!isHeroClass(classMatch[1]!)) {
        errors.push(`line ${lineNo}: unknown class kind "${classMatch[1]}" (want one of ${CLASSES.join(" | ")})`);
        cur = null;
        curAbility = null;
        continue;
      }
      cur = { kind: classMatch[1] as HeroClass, name: "", meleeWeaponEfficiencyPct: 100, magicWeaponEfficiencyPct: 100, abilities: [] };
      curAbility = null;
      classes.push(cur); // push the mutable; validated + cast at the end
      section = null;
      continue;
    }
    if (block) {
      if (seenSections.has(block[1]!)) errors.push(`line ${lineNo}: duplicate [${block[1]}] section`);
      seenSections.add(block[1]!);
      section = block[1] as "xp" | "levels";
      cur = null;
      curAbility = null;
      continue;
    }
    const abilityMatch = /^\[ability\]\s+(\d+)$/.exec(line);
    if (abilityMatch) {
      if (!cur) {
        errors.push(`line ${lineNo}: [ability] outside a [class] block`);
        curAbility = null;
        continue;
      }
      const id = Number(abilityMatch[1]);
      if (id !== cur.abilities.length) {
        errors.push(`line ${lineNo}: [ability] ${id} out of order (want ${cur.abilities.length} — slots are 0..2 in order)`);
        curAbility = null;
        continue;
      }
      curAbility = { id, name: "", kind: "", atkClass: "melee", levels: [] };
      cur.abilities.push(curAbility); // mutable kind, cast below
      continue;
    }
    if (/^\[/.test(line)) {
      errors.push(`line ${lineNo}: unknown block "${line}"`);
      section = null;
      cur = null;
      curAbility = null;
      continue;
    }

    if (section === "xp") {
      if (!/^\d+$/.test(line)) {
        errors.push(`line ${lineNo}: xp entries are bare non-negative integers (got "${line}")`);
        continue;
      }
      xp.push(Number(line));
      continue;
    }
    if (section === "levels") {
      const m = /^(\d+)\s*;\s*(\d+)\s*;\s*(\d+)\s*;\s*(\d+)$/.exec(line);
      if (!m) {
        errors.push(`line ${lineNo}: want "level ; atkBonus ; dmgMod ; maxHp" (got "${line}")`);
        continue;
      }
      levels.push({ level: Number(m[1]), atkBonus: Number(m[2]), dmgMod: Number(m[3]), maxHp: Number(m[4]) });
      continue;
    }
    if (!cur) {
      errors.push(`line ${lineNo}: field outside a [class] block`);
      continue;
    }
    const efficiency = /^(meleeWeaponEfficiencyPct|magicWeaponEfficiencyPct)\s*;\s*(.*)$/.exec(line);
    if (efficiency) {
      const key = efficiency[1] as "meleeWeaponEfficiencyPct" | "magicWeaponEfficiencyPct";
      if (curAbility || !/^\d+$/.test(efficiency[2]!) || Number(efficiency[2]) > 1000) errors.push(`line ${lineNo}: ${key} belongs to the class and must be an integer 0..1000`);
      else cur[key] = Number(efficiency[2]);
      continue;
    }
    const kv = /^(name|kind|atkClass)\s*;\s*(.*)$/.exec(line);
    if (kv) {
      if (kv[1] === "name") {
        if (curAbility) curAbility.name = kv[2]!.trim();
        else cur.name = kv[2]!.trim();
      } else if (kv[1] === "kind") {
        if (!curAbility) {
          errors.push(`line ${lineNo}: kind belongs in an [ability] block`);
          continue;
        }
        if (!ABILITY_KINDS.includes(kv[2]!.trim() as AbilityKind)) {
          errors.push(`line ${lineNo}: unknown ability kind "${kv[2]}" (want one of ${ABILITY_KINDS.join(" | ")})`);
          curAbility.kind = "next-hit-bonus";
        } else {
          curAbility.kind = kv[2]!.trim() as AbilityKind;
        }
      } else {
        if (!curAbility) {
          errors.push(`line ${lineNo}: atkClass belongs in an [ability] block`);
          continue;
        }
        const ac = kv[2]!.trim();
        if (ac !== "melee" && ac !== "magic" && ac !== "ranged") {
          errors.push(`line ${lineNo}: unknown atkClass "${ac}" (want melee | magic | ranged)`);
          continue;
        }
        curAbility.atkClass = ac;
      }
      continue;
    }
    const lvl = /^L(\d+)\s*;\s*(\d+)\s*;\s*(\d+)(?:\s*;\s*(\d+))?$/.exec(line);
    if (lvl) {
      if (!curAbility) {
        errors.push(`line ${lineNo}: L-row outside an [ability] block`);
        continue;
      }
      const n = Number(lvl[1]);
      if (n - 1 !== curAbility.levels.length) {
        errors.push(`line ${lineNo}: L${n} out of order (want L${curAbility.levels.length + 1})`);
        continue;
      }
      if (n > CLASS_MAX_LEVEL) {
        errors.push(`line ${lineNo}: L${n} above the max level ${CLASS_MAX_LEVEL}`);
        continue;
      }
      curAbility.levels.push({
        magnitude: Number(lvl[2]),
        cooldown: Number(lvl[3]),
        extra: lvl[4] === undefined ? null : Number(lvl[4]),
      });
      continue;
    }
    errors.push(`line ${lineNo}: not a recognised line: ${line}`);
  }

  // cross-section validation
  if (!seenSections.has("xp")) errors.push("missing the [xp] section");
  if (!seenSections.has("levels")) errors.push("missing the [levels] section");
  for (const kind of CLASSES) {
    if (!seenSections.has(`class:${kind}`)) errors.push(`missing the [class] ${kind} block`);
  }
  const data = { xp, levels, classes } as unknown as ClassesData;
  try {
    validateData(data);
  } catch (e) {
    errors.push(String((e as Error).message));
  }
  if (errors.length > 0) return { ok: false, errors };
  return { ok: true, data };
}

/** Canonical serialization (the knobs app's Save output; stable order so git
 *  diffs read `Barricade L3 50;520 -> 50;500`). */
export function serializeClassesStore(d: ClassesData): string {
  const out: string[] = [];
  out.push("# classes.store — the class catalog (the single source of truth).");
  out.push("#");
  out.push("# Edit by hand or via `npm run knobs` (the Classes tab). `tools/classes-store.js`");
  out.push("# parses + validates this file and emits packages/shared/src/classes.data.ts");
  out.push("# (generated, committed — never edit that one).");
  out.push("#");
  out.push("# Scope (docs/design/classes.md): three classes, five levels, three abilities");
  out.push("# each; leveling scales the abilities (and LEVEL_MODS), it never adds them.");
  out.push("# All numbers are PROVISIONAL — retune in `npm run arena` and the knobs app.");
  out.push("#");
  out.push("# Units (AGENTS §3): all integers. cooldown/duration in ticks (20 = 1 s);");
  out.push("# magnitude ×100 percents (100 = ×1.0); radius in tiles.");
  out.push("#");
  out.push("# L-row: L<n> ; magnitude ; cooldown [; extra]. `extra` (radius / duration)");
  out.push("# is REQUIRED for pull-vortex, next-hit-chain, speed-buff, damage-immune, damage-reflect");
  out.push("# (whose extra is the immunity window in ticks) and");
  out.push("# FORBIDDEN for the other kinds.");
  out.push("#");
  out.push("# phoenix-teleport: the DEATH is the gate, not the cooldown row — a cast is");
  out.push("# impossible until the hero has died on this floor, and each cast spends the");
  out.push("# charge. Keep its cooldown at 1 (a real cooldown could deny a fresh charge);");
  out.push("# its magnitude is unused.");
  out.push("");
  out.push("[xp]");
  out.push("# cumulative total XP to reach the level (line 1 = level 1)");
  for (const v of d.xp) out.push(`  ${v}`);
  out.push("");
  out.push("[levels]");
  out.push("# level ; atkBonus ; dmgMod ; maxHp   (atkBonus = attack POINTS added to the weapon; dmgMod ×100, 100 = neutral; maxHp = the bare-hero HP at the level)");
  for (const r of d.levels) out.push(`  ${r.level} ; ${r.atkBonus} ; ${r.dmgMod} ; ${r.maxHp}`);
  for (const c of d.classes) {
    out.push("");
    out.push(`[class] ${c.kind}`);
    out.push(`name ; ${c.name}`);
    out.push(`meleeWeaponEfficiencyPct ; ${c.meleeWeaponEfficiencyPct}`);
    out.push(`magicWeaponEfficiencyPct ; ${c.magicWeaponEfficiencyPct}`);
    for (const a of c.abilities) {
      out.push(`  [ability] ${a.id}`);
      out.push(`  name ; ${a.name}`);
      out.push(`  kind ; ${a.kind}`);
      out.push(`  atkClass ; ${a.atkClass}`);
      a.levels.forEach((lv, i) => {
        out.push(`  L${i + 1} ; ${lv.magnitude} ; ${lv.cooldown}${lv.extra === null ? "" : ` ; ${lv.extra}`}`);
      });
    }
  }
  return out.join("\n") + "\n";
}
